import React from 'react';
import { NavigationItem, ComponentMeta } from '../models';

interface DesktopNavigationProps {
	navigationItems: NavigationItem[];
	component?: ComponentMeta;
}

const DesktopNavigation: React.FC<DesktopNavigationProps> = ({ navigationItems, component }) => {
	const [activeDropdown, setActiveDropdown] = React.useState<number | null>(null);
	const [activeSecondaryLink, setActiveSecondaryLink] = React.useState<string | null>(null);
	const navRef = React.useRef<HTMLElement>(null);
	const buttonRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

	// Prevent background scroll when menu is open
	React.useEffect(() => {
		document.body.style.overflow = activeDropdown === null ? '' : 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, [activeDropdown]);

	// Close dropdown when clicking outside the navigation wrapper
	React.useEffect(() => {
		let cleanup = () => {};
		if (activeDropdown !== null) {
			const handleClickOutside = (event: MouseEvent) => {
				const navMenu = navRef.current;
				const targetNode = event.target as Node;
				const isInMenu = navMenu && navMenu.contains(targetNode);

				// Check if click is on the active menu link button or its children
				let isOnActiveMenuButton = false;
				if (activeDropdown !== null && buttonRefs.current[activeDropdown]) {
					const activeButton = buttonRefs.current[activeDropdown];
					const clickedButton = (targetNode as HTMLElement).closest('button');
					isOnActiveMenuButton = activeButton === clickedButton;
				}

				// Check if click is on the overlay
				const overlay = document.querySelector('.ge-header-expansion-overlay-extraspace');
				const isOnOverlay = overlay && overlay.contains(targetNode);

				if (!isInMenu || isOnActiveMenuButton || isOnOverlay) {
					setActiveDropdown(null);
					setActiveSecondaryLink(null);
				}
			};
			document.addEventListener('click', handleClickOutside);
			cleanup = () => {
				document.removeEventListener('click', handleClickOutside);
			};
		}
		return cleanup;
	}, [activeDropdown]);

		const handleNavItemClick = (item: NavigationItem, index: number, event?: React.MouseEvent) => {
			const hasSecondaryLinks = (item.headerNavigation && item.headernavigation && item.headernavigation.secondaryLinks)
				|| (item.menuLinks?.length > 0);
			if (hasSecondaryLinks) {
				const newActiveDropdown = activeDropdown === index ? null : index;
				setActiveDropdown(newActiveDropdown);
				setActiveSecondaryLink(null);
				return;
			}
			if (item.url) window.open(item.url, item.target);
		};

		const handleSecondaryLinkClick = (link: any, key: string, event: React.MouseEvent) => {
			const hasTertiaryLinks = link.tertiaryLinks && Object.keys(link.tertiaryLinks).some(k => k.startsWith('item'));
			if (hasTertiaryLinks) {
				event.preventDefault();
				const newActiveSecondary = activeSecondaryLink === key ? null : key;
				setActiveSecondaryLink(newActiveSecondary);
			} else if (link.linkUrl) {
				window.open(link.linkUrl, link.target);
			}
		};

		const getButtonTextWidth = (buttonElement: HTMLButtonElement | null): number => {
			if (!buttonElement) return 0;
			const span = document.createElement('span');
			span.style.font = '400 16px sans-serif';
			span.style.visibility = 'hidden';
			span.style.position = 'absolute';
			span.style.whiteSpace = 'nowrap';
			span.textContent = buttonElement?.textContent || '';
			document.body.appendChild(span);
			const width = span.getBoundingClientRect().width;
			span.remove();
			return width;
		};

		return (
			<nav className="ge_menu-container" ref={navRef}>
				<ul className="ge_menu-container__nav-menu-list">
					{(!navigationItems || navigationItems.length === 0) ? (
						<li className="ge_nav-menu-item">
							<span className="navigation-error-message">No navigation items found</span>
						</li>
					) : (
						navigationItems.map((item, index) => {
							const isActive = activeDropdown === index;
							const buttonClasses = `ge_nav-menu-item__nav-link ${isActive ? 'ge_nav-menu-item__nav-link--active' : ''}`;
							return (
								<li key={`nav-${item.title}-${index}`} className="ge_nav-menu-item">
									<button
										ref={(el) => buttonRefs.current[index] = el}
										aria-label={item.title}
										aria-expanded={isActive}
										className={buttonClasses}
										type="button"
										data-secondary-items-count={
											item.headerNavigation && item.headernavigation && item.headernavigation.secondaryLinks
												? Object.keys(item.headernavigation.secondaryLinks).length
												: (item.menuLinks?.length || 0)
										}
										data-link-url={item.url}
										data-link-target={item.target}
										onClick={(event) => handleNavItemClick(item, index, event)}
									>
										{item.title}
									</button>
									{isActive && (
										<div 
											className="ge_nav-menu-item__active-border"
											style={{
												width: getButtonTextWidth(buttonRefs.current[index]),
												left: '50%',
												transform: 'translateX(-50%)'
											}}
										/>
									)}
									{/* Promo cards and secondary nav panel */}
									{item.headerNavigation && item.headernavigation && isActive && (
										<div className="ge-header-secondary-nav-wrapper flex-column">
											<div className="secondary_links-background-container">
												<div className="secondary_links-container flex-row">
													{/* Secondary Links Section */}
													<div className="secondary_links flex-column">
														{item.headernavigation.secondaryLinks && Object.entries(item.headernavigation.secondaryLinks).map(([key, link]) => (
															link.linkText && <div 
																key={`header-nav-${key}`} 
																className={`secondary_link-item ${activeSecondaryLink === key ? 'secondary-active' : ''}`}
															>
																<button
																	type="button"
																	className={`menu-label secondary-link-button ${activeSecondaryLink === key ? 'secondary-link-active' : ''}`}
																	aria-label={link.linkText}
																	aria-expanded={link.tertiaryLinks && Object.keys(link.tertiaryLinks).some(key => key.startsWith('item')) ? activeSecondaryLink === key : undefined}
																	onClick={(event) => handleSecondaryLinkClick(link, key, event)}
																>
																	{link.linkText}
																	{link.tertiaryLinks && Object.keys(link.tertiaryLinks).some(key => key.startsWith('item')) && (
																		link.linkIcon ? (
																			<span className="link-icon" dangerouslySetInnerHTML={{ __html: link.linkIcon }} />
																		) : null
																	)}
																</button>
															</div>
														))}
													</div>
													{/* Right panel: promo cards or tertiary links */}
													<div className="secondary_link-expansion">
														<div className="tertiary_links-container">
															{/* If activeSecondaryLink and tertiary links exist, show tertiary links */}
															{activeSecondaryLink && item.headernavigation.secondaryLinks && item.headernavigation.secondaryLinks[activeSecondaryLink]?.tertiaryLinks ? (
																<div className="tertiary_links flex-column">
																	{Object.entries(item.headernavigation.secondaryLinks[activeSecondaryLink].tertiaryLinks)
																		.filter(([tertiaryKey]) => tertiaryKey.startsWith('item'))
																		.map(([tertiaryKey, tertiary]) => (
																		<a
																			key={`tertiary-${activeSecondaryLink}-${tertiaryKey}`}
																			href={tertiary.linkUrl}
																			target={tertiary.target}
																			className="tertiary_link-item"
																			aria-label={tertiary.linkText}
																		>
																			{tertiary.linkText}
																		</a>
																	))}
																</div>
															) : (
																(() => {
																	// Only show promo cards if NO secondary link is active with tertiary links
																	const secondaryPromoItemsRaw = item.headernavigation.secondaryPromoItems;
																	// Convert object to array if needed
																	let secondaryPromoItems: any[] = [];
																	if (Array.isArray(secondaryPromoItemsRaw)) {
																		secondaryPromoItems = secondaryPromoItemsRaw;
																	} else if (secondaryPromoItemsRaw && typeof secondaryPromoItemsRaw === 'object') {
																		secondaryPromoItems = Object.values(secondaryPromoItemsRaw).filter(v => typeof v === 'object' && v !== null && !Array.isArray(v));
																	}
																	const showPromo = !(
																		activeSecondaryLink &&
																		item.headernavigation.secondaryLinks &&
																		item.headernavigation.secondaryLinks[activeSecondaryLink]?.tertiaryLinks &&
																		Object.keys(item.headernavigation.secondaryLinks[activeSecondaryLink].tertiaryLinks || {}).some(k => k.startsWith('item'))
																	);

																	const getImageUrl = (fileReference: any) => {
																		if (!fileReference) return '';
																		if (typeof fileReference === 'object' && fileReference.assetName) {
																		const assetName = fileReference.assetName;
																		if (typeof assetName === 'string' && assetName.startsWith('/images/')) {
																			return assetName;
																		}
																		return `/images/gehealthcarestage/${assetName}`;
																		}
																		return '/images/generic-default-image.jpg';
																	};
																	return secondaryPromoItems.length > 0 && showPromo ? (
																		<div className="ge-header-promo-cards-container flex-column display-on">
																			{item.headernavigation.secondaryPromoTitle && (
																				<div className="title-xs" dangerouslySetInnerHTML={{ __html: item.headernavigation.secondaryPromoTitle }} />
																			)}
																			<div className={`ge-header-promo-cards layout-grid-${secondaryPromoItems.length}`}>
																				{secondaryPromoItems.map((promo: any, idx: number) => (
																					<button
																						key={`promo-card-${promo.linkUrl || promo.cardTitle || idx}`}
																						aria-label="Promo Card"
																						className={`ge-header-promo-card flex-column ${promo.isPromoCardUnClickable === 'true' ? 'non-clickable' : 'clickable'} trackable`}
																						data-link-url={promo.linkUrl}
																						data-link-target={promo.target}
																						type="button"
																						disabled={promo.isPromoCardUnClickable === 'true'}
																						onClick={() => {
																							if (promo.linkUrl && promo.isPromoCardUnClickable !== 'true') {
																								window.open(promo.linkUrl, promo.target);
																							}
																						}}
																					>
																						<div className="card-media-wraper">
																							{promo.cardImage && (
																								<img src={getImageUrl(promo.cardImage)} alt="Promo Card" style={{ width: '100%' }} />
																							)}
																						</div>
																						<div className="flex-column gap-8">
																							{promo.cardTitle && (
																								<div className="text-paragraph-s card-title" dangerouslySetInnerHTML={{ __html: promo.cardTitle }} />
																							)}
																							{promo.cardBodyCopy && (
																								<div className="text-paragraph-s" dangerouslySetInnerHTML={{ __html: promo.cardBodyCopy }} />
																							)}
																						</div>
																					</button>
																				))}
																			</div>
																		</div>
																	) : null;
																})()
															)}
														</div>
													</div>
												</div>
											</div>
											<div className="ge-header-expansion-overlay-extraspace"></div>
										</div>
									)}
									{/* Regular menuLinks dropdown (for items without headerNavigation) */}
									{!item.headerNavigation && item.menuLinks && item.menuLinks.length > 0 && isActive && (
										<div className="ge-header-secondary-nav-wrapper flex-column">
											<div className="secondary_links-background-container">
												<div className="secondary_links-container flex-row">
													<div className="secondary_links flex-column">
														{item.menuLinks.map((secondary, secIndex) => (
															<div key={`sub-${secondary.title}-${secIndex}`} className="secondary_link-item">
																<a
																	href={secondary.generalLink?.url}
																	target={secondary.generalLink?.target}
																	className="menu-label"
																	aria-label={secondary.title}
																>
																	{secondary.title}
																</a>
															</div>
														))}
													</div>
												</div>
											</div>
											<div className="ge-header-expansion-overlay-extraspace"></div>
										</div>
									)}
								</li>
							);
						})
					)}
				</ul>
				<div className="ge_nav-menu-item__active-menu-border"></div>
			</nav>
		);
};

export default DesktopNavigation;